import { getLatestFinishedCommitData } from 'lib/documentHandling';
import moment from 'moment';
import { path } from 'ramda';
import calculateEQ5DTotalValue, {
  calculateEQ5DVAS,
} from '../../QualityOfLife/components/CalculatorField/calculateEQ5D';
import { statusSections } from '../../Status/Utils';

const addDayDelayToDate = (date, dayDelay = 3) => moment.utc(date).add(dayDelay, 'd');
const addMonthDelayToDate = (date, monthDelay = 3) => moment.utc(date).add(monthDelay, 'M');

/**
 * Determine the current online rehab session "step" for stepper
 * "As a general rule the patient is able to move on to the next rehabilitation session
 *   three days after completing the previous one."
 * "The patient shall not be able to move on to the fourth/sixth rehabilitation session
 *   until the neuropsychologist has given feedback"
 * "Session 2 finished: The patient has completed the seven days reporting to the fatigue tracking diary"
 * @param doc - Online rehab document
 * @returns Which session step should be active
 */
export const currentStep = (doc, fatigueDiaryCompleted) => {
  let step = 0;
  const currentDate = moment();
  // Development mode, to skip day delay, waiting for therapist comments and waiting for fatigue diary
  const isDev = __ENABLE_PROD_TESTING__;

  const dayDelay = 3;
  const monthDelay = 3;

  if (path(['start'], doc)) step++;
  if (path(['rehab1', 'finished'], doc)) {
    const finishedDate = path(['rehab1', 'finished'], doc);
    if (isDev || currentDate.isAfter(addDayDelayToDate(finishedDate, dayDelay))) {
      // After 3 days the next step is available
      step++;
    }
  }
  if (step === 2 && path(['rehab2', 'finished'], doc)) {
    const finishedDate = path(['rehab2', 'finished'], doc);
    if (isDev || (currentDate.isAfter(addDayDelayToDate(finishedDate, dayDelay)) && fatigueDiaryCompleted)) {
      step++;
    }
  }
  if (step === 3 && path(['rehab3', 'finished'], doc)) {
    if (isDev || path(['rehab3', 'neurologistsComments'], doc)) {
      // After neuropsychologists comments have been received
      step++;
    }
  }
  if (step === 4 && path(['rehab4', 'finished'], doc)) {
    const finishedDate = path(['rehab4', 'finished'], doc);
    if (isDev || currentDate.isAfter(addDayDelayToDate(finishedDate, dayDelay))) {
      step++;
    }
  }
  if (step === 5 && path(['rehab5', 'finished'], doc)) {
    if (isDev || path(['rehab5', 'neurologistsComments'], doc)) {
      step++;
    }
  }
  if (step === 6 && path(['rehab6', 'finished'], doc)) {
    const finishedDate = path(['rehab6', 'finished'], doc);
    if (isDev || currentDate.isAfter(addDayDelayToDate(finishedDate, dayDelay))) {
      step++;
    }
  }
  if (step === 7 && path(['rehab7', 'finished'], doc)) {
    const finishedDate = path(['rehab7', 'finished'], doc);
    if (isDev || currentDate.isAfter(addMonthDelayToDate(finishedDate, monthDelay))) {
      step++;
    }
  }

  return step;
};

export const isStatusDone = doc => {
  const keys = Object.keys(statusSections);
  const allFieldsFilled = keys.every(k => {
    const value = path([k], doc);
    return value !== undefined && value !== null;
  });
  return allFieldsFilled;
};
export const isEq5dDone = doc => {
  return !!(calculateEQ5DTotalValue(doc) && calculateEQ5DVAS(doc) !== 999);
};
export const isMsnqDone = doc => {
  const qMatch = /question.*/;
  const msnqQuestions = doc.filter(key => qMatch.test(key));
  return msnqQuestions ? msnqQuestions.length === 15 : false;
};
export const isFsmcDone = doc => {
  const qMatch = /question.*/;
  const fsmcQuestions = doc.filter(key => qMatch.test(key));
  return fsmcQuestions ? fsmcQuestions.length === 20 : false;
};

export const isDocumentDone = (commit, type) => {
  if (!commit || !commit.data || typeof commit.data !== 'object' || Array.isArray(commit.data)) return false;

  switch (type) {
    case 'status':
      return isStatusDone(commit.data.sections || {});
    case 'eq5d':
      return isEq5dDone(commit.data.sections);
    case 'msnq':
      return isMsnqDone(Object.keys(commit.data));
    case 'fsmc':
      return isFsmcDone(Object.keys(commit.data));
    default:
      return false;
  }
};

export const timeLeft = date => {
  return moment.utc(date).add(3, 'd').fromNow();
};
export const timeLeftFollowup = date => {
  return moment.utc(date).add(3, 'M').fromNow();
};

export const hasDiaryBeenFilled = (documents, onlineRehabStartDate) => {
  const fatigueTrackingDiaryLatest = documents.find(
    d => d.documentType === 'fatigueTrackingDiary' && d.createDate > onlineRehabStartDate,
  );
  const diaryData = fatigueTrackingDiaryLatest && getLatestFinishedCommitData(fatigueTrackingDiaryLatest);
  // See if last day of diary has some other data than date
  const hasDiaryBeenFilled = (diaryData && diaryData[6] && Object.keys(diaryData[6]).length > 1) || false;

  return hasDiaryBeenFilled;
};
